import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Key, EnvelopeSimple, LockKey, UserPlus, ArrowLeft } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FieldGroup = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-stone-700 font-bold uppercase text-[11px] tracking-wide">{label}</Label>
    {children}
  </div>
);

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [view, setView] = useState('login');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (otpCooldown <= 0) { clearInterval(cooldownRef.current); return; }
    cooldownRef.current = setInterval(() => {
      setOtpCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [otpCooldown]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      login(res.data.user, res.data.token);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleRequestOtp = async () => {
    if (!email) return toast.error('Enter your email first');
    if (otpCooldown > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/request-otp`, { email });
      if (res.data.email_sent) {
        toast.success(res.data.message || 'OTP sent', { duration: 5000 });
        setOtpSent(true);
        setOtpCooldown(res.data.cooldown || 60);
      } else {
        toast.error(res.data.message || 'Failed to send OTP');
        if (res.data.cooldown) setOtpCooldown(res.data.cooldown);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { email, otp }, { withCredentials: true });
      login(res.data.user, res.data.token);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email, name, password });
      toast.success('Registration submitted! Awaiting admin approval.');
      setView('login');
      setName(''); setPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleResetRequest = async () => {
    if (!email) return toast.error('Enter your email first');
    if (otpCooldown > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password/request`, { email });
      if (res.data.email_sent) {
        toast.success(res.data.message || 'OTP sent', { duration: 5000 });
        setResetStep(2);
        setOtpCooldown(res.data.cooldown || 60);
      } else {
        toast.error(res.data.message || 'Failed to send OTP');
        if (res.data.cooldown) setOtpCooldown(res.data.cooldown);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password/confirm`, { email, otp, new_password: newPassword });
      toast.success('Password reset! You can now login.');
      setView('login');
      setOtp(''); setNewPassword(''); setResetStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  const switchView = (v) => {
    setView(v);
    setOtp(''); setOtpSent(false); setResetStep(1); setNewPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2D1810] to-[#1A1A1A] flex">
      {/* Left Hero — desktop only */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Noodles" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/60 to-[#E31837]/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white p-8 animate-fade-in-up">
            <h2 className="font-heading text-6xl font-extrabold mb-3 drop-shadow-lg tracking-tight">KBONG</h2>
            <p className="text-2xl font-medium drop-shadow-md">Premium Quality Noodles</p>
            <p className="text-lg mt-2 opacity-80">From Bhutan with Love</p>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
          {/* Orange top bar */}
          <div className="h-1.5 bg-gradient-to-r from-[#FF6B00] to-[#E31837]" />

          <div className="p-6 sm:p-8">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <img src="/kbong-logo.png" alt="Kbong" className="w-14 h-14 object-contain" />
              <div>
                <h1 className="font-heading font-extrabold text-2xl text-[#1A1A1A] tracking-tight leading-none">KBONG</h1>
                <p className="text-[11px] text-[#FF6B00] font-bold tracking-wider">BHUTAN FOOD PVT. LTD.</p>
              </div>
            </div>

            {/* ===== PASSWORD LOGIN ===== */}
            {view === 'login' && (
              <div className="animate-fade-in-up">
                <h2 className="font-heading font-extrabold text-2xl text-stone-900 mb-1 tracking-tight" data-testid="login-title">Sign In</h2>
                <p className="text-stone-500 text-sm mb-6">Access your factory dashboard</p>
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <FieldGroup label="Email">
                    <Input data-testid="email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="name@kbong.com" />
                  </FieldGroup>
                  <FieldGroup label="Password">
                    <Input data-testid="password-input" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" />
                  </FieldGroup>
                  <Button data-testid="submit-button" type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base">
                    {loading ? 'Signing in...' : 'Sign In'} <ArrowRight weight="bold" size={18} className="ml-1" />
                  </Button>
                </form>
                <button onClick={() => switchView('reset')} className="text-sm text-[#FF6B00] font-bold mt-4 block transition-colors duration-200 hover:text-[#E31837]" data-testid="forgot-password-link">Forgot Password?</button>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
                  <div className="relative flex justify-center"><span className="px-3 bg-white text-stone-400 text-xs font-bold">OR</span></div>
                </div>

                <Button onClick={() => switchView('otp')} variant="outline" className="w-full h-12 font-bold text-stone-800" data-testid="otp-login-btn">
                  <Key weight="bold" size={18} className="mr-2 text-[#FF6B00]" /> Login with OTP
                </Button>

                <div className="text-center mt-5">
                  <button onClick={() => switchView('register')} className="text-stone-500 text-sm transition-colors duration-200 hover:text-[#FF6B00]" data-testid="register-link">
                    Don't have an account? <span className="font-bold text-[#FF6B00] underline underline-offset-2">Register</span>
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-stone-200 text-center">
                  <button onClick={() => window.location.href = '/kiosk'} className="flex items-center justify-center gap-2 w-full h-11 bg-stone-900 text-[#FFD100] rounded-xl text-xs font-bold uppercase transition-transform duration-200 active:scale-[0.97] hover:bg-stone-800" data-testid="kiosk-mode-link">
                    <LockKey weight="bold" size={16} /> Kiosk Mode
                  </button>
                </div>
              </div>
            )}

            {/* ===== OTP LOGIN ===== */}
            {view === 'otp' && (
              <div className="animate-fade-in-up">
                <button onClick={() => switchView('login')} className="flex items-center gap-1.5 text-stone-500 text-sm font-bold mb-4 transition-colors duration-200 hover:text-stone-800"><ArrowLeft size={14} weight="bold" /> Back</button>
                <h2 className="font-heading font-extrabold text-2xl text-stone-900 mb-1 tracking-tight">OTP Login</h2>
                <p className="text-stone-500 text-sm mb-6">We'll send a one-time code to your email</p>
                {!otpSent ? (
                  <div className="space-y-4">
                    <FieldGroup label="Email">
                      <Input data-testid="otp-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@kbong.com" />
                    </FieldGroup>
                    <Button onClick={handleRequestOtp} disabled={loading || otpCooldown > 0} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base" data-testid="send-otp-btn">
                      {loading ? 'Sending...' : otpCooldown > 0 ? `Wait ${otpCooldown}s` : 'Send OTP'} <EnvelopeSimple weight="bold" size={18} className="ml-1" />
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="bg-green-50 text-green-700 text-sm font-bold px-4 py-3 rounded-xl border border-green-200">OTP sent to {email}</div>
                    <FieldGroup label="Enter OTP">
                      <Input data-testid="otp-code-input" type="text" inputMode="numeric" maxLength={6} required value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} className="text-center text-2xl tracking-[0.5em] font-mono" placeholder="------" />
                    </FieldGroup>
                    <Button type="submit" disabled={loading || otp.length < 6} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base" data-testid="verify-otp-btn">
                      {loading ? 'Verifying...' : 'Verify & Login'}
                    </Button>
                    <button type="button" onClick={() => { if (otpCooldown > 0) return; setOtpSent(false); setOtp(''); handleRequestOtp(); }} disabled={otpCooldown > 0} className={`text-sm font-bold w-full text-center mt-1 transition-colors duration-200 ${otpCooldown > 0 ? 'text-stone-300 cursor-not-allowed' : 'text-stone-500 hover:text-[#FF6B00]'}`} data-testid="resend-otp-btn">
                      {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Resend OTP'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* ===== REGISTER ===== */}
            {view === 'register' && (
              <div className="animate-fade-in-up">
                <button onClick={() => switchView('login')} className="flex items-center gap-1.5 text-stone-500 text-sm font-bold mb-4 transition-colors duration-200 hover:text-stone-800"><ArrowLeft size={14} weight="bold" /> Back</button>
                <h2 className="font-heading font-extrabold text-2xl text-stone-900 mb-1 tracking-tight">Register</h2>
                <p className="text-stone-500 text-sm mb-6">Your account will be created upon admin approval</p>
                <form onSubmit={handleRegister} className="space-y-4">
                  <FieldGroup label="Full Name">
                    <Input data-testid="register-name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
                  </FieldGroup>
                  <FieldGroup label="Email">
                    <Input data-testid="register-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="name@kbong.com" />
                  </FieldGroup>
                  <FieldGroup label="Password">
                    <Input data-testid="register-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" />
                  </FieldGroup>
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base" data-testid="register-submit">
                    {loading ? 'Submitting...' : 'Submit Registration'} <UserPlus weight="bold" size={18} className="ml-1" />
                  </Button>
                </form>
                <div className="text-center mt-5">
                  <button onClick={() => switchView('login')} className="text-stone-500 text-sm transition-colors duration-200 hover:text-[#FF6B00]">
                    Already have an account? <span className="font-bold text-[#FF6B00] underline underline-offset-2">Sign In</span>
                  </button>
                </div>
              </div>
            )}

            {/* ===== RESET PASSWORD ===== */}
            {view === 'reset' && (
              <div className="animate-fade-in-up">
                <button onClick={() => switchView('login')} className="flex items-center gap-1.5 text-stone-500 text-sm font-bold mb-4 transition-colors duration-200 hover:text-stone-800"><ArrowLeft size={14} weight="bold" /> Back</button>
                <h2 className="font-heading font-extrabold text-2xl text-stone-900 mb-1 tracking-tight">Reset Password</h2>
                <p className="text-stone-500 text-sm mb-6">We'll send a code to verify your identity</p>
                {resetStep === 1 ? (
                  <div className="space-y-4">
                    <FieldGroup label="Email">
                      <Input data-testid="reset-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@kbong.com" />
                    </FieldGroup>
                    <Button onClick={handleResetRequest} disabled={loading || otpCooldown > 0} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base" data-testid="reset-send-otp">
                      {loading ? 'Sending...' : otpCooldown > 0 ? `Wait ${otpCooldown}s` : 'Send OTP'} <LockKey weight="bold" size={18} className="ml-1" />
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleResetConfirm} className="space-y-4">
                    <div className="bg-green-50 text-green-700 text-sm font-bold px-4 py-3 rounded-xl border border-green-200">OTP sent to {email}</div>
                    <FieldGroup label="Enter OTP">
                      <Input data-testid="reset-otp" type="text" inputMode="numeric" maxLength={6} required value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} className="text-center text-2xl tracking-[0.5em] font-mono" placeholder="------" />
                    </FieldGroup>
                    <FieldGroup label="New Password">
                      <Input data-testid="reset-new-password" type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
                    </FieldGroup>
                    <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-[#FF6B00] to-[#E31837] text-white font-bold rounded-xl text-base" data-testid="reset-confirm-btn">
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                    <button type="button" onClick={() => { if (otpCooldown > 0) return; handleResetRequest(); }} disabled={otpCooldown > 0} className={`text-sm font-bold w-full text-center mt-1 transition-colors duration-200 ${otpCooldown > 0 ? 'text-stone-300 cursor-not-allowed' : 'text-stone-500 hover:text-[#FF6B00]'}`} data-testid="reset-resend-otp-btn">
                      {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Resend OTP'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
